import { useMultiDoc } from 'global/cnr/contexts/useHelpers/useMultiDoc';
import _ from 'lodash';
import React, { createContext, Suspense, useContext, useEffect, useMemo, useReducer, useState } from 'react';
import { Icon, Label, Sidebar } from 'semantic-ui-react';
import { ParentContext } from '../../../global/cnr/contexts/ParentContext';
import { DimmerPending } from '../../../global/components/alerts/DimmerWrap';
import SuspenseDimmer from '../../../global/components/alerts/SuspenseDimmer';
import UiSaveButtons from '../../../global/components/buttons/UiSaveButtons';
import MenuVert from '../../../global/components/menus/MenuVert';
import DataSidebar from '../../../global/components/swipers/DataSidebar';
import TreeViewer from '../../../global/components/viewers/TreeViewer';
import { gEnums } from '../../../global/enums/globalEnums';
import { listYears } from '../../../global/lists/lists';
import { openExternal } from '../../../global/viewSettings/helpers/settingsLinks';
import Wrapper, { wrapperTypes } from '../../../global/wrappers/Wrapper';
import WrapperVertCols from '../../../global/wrappers/WrapperVertCols';
import { SportsDistrictContext } from '../cnr/contexts/SportsDistrictContext';
import { SportsSeasonContext } from '../cnr/contexts/SportsSeasonContext';
import { createMediaSeasonHandlers, createMediaSeasonInitialState, createMediaSeasonReducer } from '../cnr/reducers/CreateMediaSeasonReducer';
import { createScheduleTypes } from '../cnr/reducers/CreateSportsReducer';
import { createSportsSeasonHandlers, createSportsSeasonInitialState, createSportsSeasonReducer } from '../cnr/reducers/CreateSportsSeasonReducer';
import { seasonalHelpers, seasonalUpdateTypes } from '../cnr/reducers/reducerHelpers/seasonalSportsData';
import { dataItems_sportsSeason_create } from '../cnr/reducers/SportsSeasonReducer';
import { _sportCollections } from '../cnr/reducers/SportsSidebarReducer';
import { _sdm } from '../elements/dashboard/SportsDashboard';
import { _fetchModes } from '../elements/matches/MatchDataUpdate';
import { _allowUpdates_sports, useCreateSeasonData } from '../useHelpers/useCreateSeasonData';
import { useGoogleSports } from '../useHelpers/useGoogleSports';
import { useMedia } from '../useHelpers/useMedia';
import { useSeasonalData } from '../useHelpers/useSeasonals';
import { useSportsFilters } from '../useHelpers/useSportsFilters';
import { CreateSportsContext } from './CreateSports';
import CreateSportSeasonAlignment from './CreateSportSeasonAlignment';
import CreateSportSeasonTeams from './CreateSportSeasonTeams';
import CreateCrossoverSections from './subs/CreateCrossoverSections';
import CreateSportsDetails from './subs/CreateSportsDetails';
import { FooterExternal } from './subs/FooterExternal';
import { PopupSelect } from 'global/components/popups/PopupSelect';
import { createRefPath_event } from 'global/firestoreData/appData/appRefPaths';
import { useFetchMedia } from '../useHelpers/useFetchMedia';
import { convertHelpers } from 'global/common/convert';

export const CreateSportsSeasonContext = createContext();
export const CreateMediaSeasonContext = createContext();

const CreateSchedule = React.lazy(() => import("./subs/CreateSchedule"));
const CreateSectionDates = React.lazy(() => import("./subs/CreateSectionDates"));

const _csv_match = ['id', '_itemKey', 'startDate', 'level', 'section', 'combined', 'away', 'awayScore', 'home', 'homeScore', 'extra', 'oldStartDate', 'postponed', 'cancelled', 'exhibition']
const _csv_matchPlayoff = ['id', 'startDate', 'startTime', 'level', 'poolKey', 'matchNumber', 'location', 'awaySeed', 'away', 'awayScore', 'homeSeed', 'home', 'homeScore', 'away1', 'home1', 'away2', 'home2', 'away3', 'home3', 'away4', 'home4', 'away5', 'home5']
const _csv_team = ['id', '_itemKey', 'name', 'levels', 'sections']

const _steps = {
  year: 'year',
  teams: 'teams',
  alignment: 'alignment',
  sectionCrossovers: 'sectionCrossovers',
  details: 'details',
  schedule: 'schedule',
}

const _rightModes = {
  currentSport: 'currentSport',
  currentSeason: 'currentSeason',
  currentMatches: 'currentMatches',
  currentTeams: 'currentTeams',
  commitCurrent: 'commitCurrent',
  commitSeasonMatches: 'commitSeasonMatches',
  commitSeasonPlayoffMatches: 'commitSeasonPlayoffMatches',
  commitSeasonTeams: 'commitSeasonTeams',
  commitSeasonTeamsAndMatches: 'commitSeasonTeamsAndMatches',
  createMediaSeason: 'createMediaSeason',
  createMediaSeasonAlignment: 'createMediaSeasonAlignment',
  createMediaSeasonFull: 'createMediaSeasonFull',
  createMediaSeasonMatches: 'createMediaSeasonMatches',
  createMediaSeasonRecords: 'createMediaSeasonRecords',
  createMediaSeasonTeams: 'createMediaSeasonTeams',
  exportMatches: 'exportMatches',
  exportPlayoffMatches: 'exportPlayoffMatches',
  exportTeams: 'exportTeams',
  // teamsAndSections: 'teamsAndSections',
}

const _icons = {
  open: 'external alternate'
}

const _autoUpdateSteps = false

const CreateSeason = (props) => {

  const _asat = gEnums.availableSubActionTypes

  const { subActionType, handleActionPreview, viewItem, handleCancel } = props

  // parentContext
  const parentContext = useContext(ParentContext);
  const { states, fns, fn_cls } = parentContext ?? {}
  const { app_state, paps_state } = states ?? {}
  const { mainDb } = app_state ?? {}
  const { database_fns } = fns ?? {}
  const { pathViews } = paps_state ?? {}

  // sportsDistrictContext
  const sportsDistrictContext = useContext(SportsDistrictContext)
  const { sportsDistrict_state } = sportsDistrictContext ?? {}
  const { district_data } = sportsDistrict_state ?? {}
  const { sports, gls_scores } = district_data ?? {}

  // sportsSeasonContext -> is a wrapper around all creates
  const sportsSeasonContext = useContext(SportsSeasonContext)
  const { sportsSeason_state, sportsSeason_handlers } = sportsSeasonContext ?? {}
  const {
    currentSeason_info,
    links_score,
    matches_info,
    parentDataKeys,
    sportPermissions,
  } = sportsSeason_state ?? {}

  console.log('sportsSeason_state', sportsSeason_state)

  const { latestSeason, _sportsAbr, _sportsModeKey, _sportsModeSport } = currentSeason_info ?? {}
  const { matches } = matches_info ?? {}

  // createSportsContext
  const createSportsContext = useContext(CreateSportsContext);
  const { createSports_state, createSports_handlers } = createSportsContext ?? {}
  const {
    currentSport,
    qualifyReady,
    sportsKey,
    teams,
    teams_info,
    updating: updating_sports,
    _seasonalKey,
  } = createSports_state ?? {}


  const googleSports = useGoogleSports({ sportsDataMode: _sdm.sports.seasonResults })
  const { handleGet_gsMatches } = googleSports ?? {}

  const {
    ammend_sportsSeasonalItem,
    ammend_sportsTeams,
    update_seasonalData,
    datasToUpdate
  } = useCreateSeasonData()

  const _seasonalDataHelpers = useSeasonalData()
  const { getLevelTeams, getHomeAways, getSectionDates } = useSportsFilters()
  const { handleGet_multiDocs } = useMultiDoc()

  const {
    getMedia_records,
    getMedia_results,
    getMedia_alignments,
    getMedia_alignmentsAndResults,
    ammendMedia_season,
    getMediaCdm,
    mediaSeason_local,
    mediaResults_local,
    mediaAlignment_local,
    updateMedia_localResults,
    updateMedia_localAlignment,
  } = useMedia({ database_fns, pathViews, links_score, teams, sportsModeSport: _sportsModeSport, sportsKey, sportsAbr: _sportsAbr })

  const { getMedia_resultsOnly } = useFetchMedia()

  const init_state_ss = {
    // database_fns,
    parentDataKeys,
    pathViews,
    currentSeason_info,
    sportsData_info: {
      sports,
      sportPermissions,
      sportsKey,
      _sportsAbr,
      _seasonalKey,
    },
    // mainDb,
  }
  const [createSportsSeason_state, createSportsSeason_dispatch] = useReducer(createSportsSeasonReducer, init_state_ss, createSportsSeasonInitialState)
  const createSportsSeason_handlers = createSportsSeasonHandlers(createSportsSeason_dispatch, createSportsSeason_state)
  const {
    sportsData_info,
    selectedItems,
    _seasonalId,
    sportsSeason_selected,
    sportsSeason_selectedPdks,
    sportsSeason_db,
    updating: updating_create,
  } = createSportsSeason_state ?? {}

  const { sportSeasonals } = sportsData_info ?? {}
  const { seasonals_fluid, seasonals, matches: matches_create } = sportsSeason_selected ?? {}
  const { details } = seasonals ?? {}
  const { defaultFromLastSeason, seasonStartDate } = details ?? {}

  const { year: selectedYear, seasonal: selectedSeasonal } = selectedItems ?? {}

  const _sportSeasonalsKeys = sportSeasonals ? _.keys(sportSeasonals) : []
  const _sportsYearAbr = _sportsAbr + '_' + selectedYear

  useEffect(() => {
    ammend_sportsSeasonalItem(seasonals)
  }, [seasonals]);

  useEffect(() => {
    ammend_sportsTeams(seasonals_fluid?.sportSeasonTeams)
  }, [seasonals_fluid?.sportSeasonTeams]);

  useEffect(() => {
    const { teams, seasonal, matches } = datasToUpdate
    const sportInfo = {
      seasonals: seasonal, // YES 
      selectedSeason: selectedYear,
      teams_seasonal: {
        teams
      },
    }
    const sportsResultsInfo = {
      matchInfo_seasonal: {
        matches,
      }
    }
    const sportsData = {
      sportInfo,
      sportsResultsInfo
    }
    if (teams && seasonal && matches) {
      sportsSeason_handlers.handleSet_sportsInfoAndResults(sportsData)

    }
  }, [datasToUpdate]);


  const init_state_ms = {
    parentDataKeys,
    pathViews,
    database_fns,
    sportsKey,
    sportPermissions,
    _sportsAbr,
    sports,
    teams_info,
    database_fns,
  }

  const updateColor = _allowUpdates_sports && sportPermissions.any ? 'green' : 'teal'

  const [createMediaSeason_state, createMediaSeason_dispatch] = useReducer(createMediaSeasonReducer, init_state_ms, createMediaSeasonInitialState)
  const createMediaSeason_handlers = createMediaSeasonHandlers(createMediaSeason_dispatch, createMediaSeason_state)
  const { mediaSeason, mediaSeasonalData_current, updating: updating_media } = createMediaSeason_state

  const _updating = updating_media || updating_create || updating_sports

  const links_sport = gls_scores ? gls_scores[sportsKey] : null
  const { districtLink, googleSheetsId, mediaUrl } = links_sport ?? {}

  const _currentSport = _sportsModeKey ? _.startCase(_sportsModeKey) : currentSport


  // local state  
  let steps = []

  switch (subActionType) {
    case _asat.createSeason:
      steps = [
        _steps.details,
        _steps.teams,
        _steps.alignment,
        _steps.sectionCrossovers,
        _steps.schedule,
      ]
      break;
    case _asat.editSeason:
      steps = [
        _steps.details,
      ]
      break;
  }


  const d = new Date()

  const [mode_right, setMode_right] = useState()
  const [selected_right, setSelected_right] = useState()
  const [selected_rightSeasonal, setSelected_rightSeasonal] = useState()
  const [stepValidations, setStepValidations] = useState()
  const [step, setStep] = useState({ index: 0, name: steps[0] })
  const [upd, setUdp] = useState()
  const stepCount = steps.length

  const setInit = () => {
    const { sportsTeams } = district_data ?? {}
    const teams_district = _.cloneDeep(sportsTeams?.[sportsKey]?.teams)

    const teams_sport = _.cloneDeep(teams)

    _.forEach(teams_district, (team_district, tdk) => {
      team_district._selected = teams_sport[tdk] ? true : false
    })

    _.forEach(teams_sport, (team_sport) => {
      team_sport._selected = true
    })

    createSportsSeason_handlers.handleAmmend_districtTeams(teams_district)
    createSportsSeason_handlers.handleInit_sportsTeams(teams_sport)
  }

  useEffect(() => {
    getMediaCdm(2026);
    const cbsd = (dataResult) => { createSportsSeason_handlers.handleResults_sportsData(dataResult) }
    const cbss = (dataResult) => { createSportsSeason_handlers.handleResults_sportSeasonals(dataResult) }
    // get the data
    _seasonalDataHelpers.createSeasonInit(sportsKey, cbsd) // creates the initial data => cbsd
    _seasonalDataHelpers.getSportSeasonals(cbss)
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, []);

  useEffect(() => {
    setInit()
  }, [district_data, teams]);

  useEffect(() => {
    // change the year > get the ata
    if (selectedYear) {
      const cbn = (seasonalData, _parentDataKeys) => {
        createSportsSeason_handlers.handleAmmend_sportsSeason_selected(seasonalData, _parentDataKeys)
      }
      const _pks = seasonalHelpers.getPks(pathViews, selectedYear, pathViews.sports)
      createSportsSeason_handlers.handleAmmend_sportsSeason_selected()
      handleGet_multiDocs({ key: 'sportsInfo', sportsDocs: dataItems_sportsSeason_create, pathViews, pks: _pks, callback: cbn })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [selectedYear]);

  useEffect(() => {
    if (defaultFromLastSeason) {
      const cbls = (res) => { createSportsSeason_handlers.handleResults_seasonalData(res) }
      _seasonalDataHelpers.getSeasonalData(sportsKey, latestSeason, cbls)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [defaultFromLastSeason]);

  useEffect(() => {
    if (mediaSeason) {
      createSportsSeason_handlers.handleMedia_ammendSeasonalFromMedia(mediaSeasonalData_current, matches_info)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [mediaSeason]);

  const handleExport = async (values, tabName) => {
    createSports_handlers.handleStartUpdate()
    await fn_cls.updateGoogleSheetValues(googleSheetsId, tabName, values)
    createSports_handlers.handleStopUpdate()
  }

  const handleActive_year = (item) => createSportsSeason_handlers && createSportsSeason_handlers.handleAmmend_selectedYearOrSeasonal('year', item.name)
  const handleActive_seasonal = (value) => createSportsSeason_handlers && createSportsSeason_handlers.handleAmmend_selectedYearOrSeasonal('seasonal', value)

  // const createGoogleSheet = () => {
  //   fn_cls.createGoogleSpreadsheet('Testing 123').then(response_data => {
  //     const { success } = response_data ?? {}
  //     console.log('response_data', response_data)
  //     console.log('SUCCESS', success)
  //   })
  // }

  const handleClick_getGoogle_results = () => {
    const cbgr = (r) => { createMediaSeason_handlers.handleStopUpdate() }
    createMediaSeason_handlers.handleStartUpdate()
    const opts = { fetchMode: _fetchModes.resultsMedia, latestSeason: selectedYear }
    handleGet_gsMatches({ opts, callback: cbgr })
  }


  const handleClick_getMedia_results = () => {
    const cb1 = (results) => {
      createMediaSeason_handlers.handleStopUpdate()
    }
    createMediaSeason_handlers.handleStartUpdate()
    const callData = {
      year: selectedYear,
      yearAdditional: 0,
      useSingleDate: true,
      singleDate: '02/06/2026'
    }
    getMedia_resultsOnly(callData, true)
    // getMedia_results({ sportsYear: selectedYear, yearAdditional: 0, sportsModeSport: _sportsModeSport, callback: cb1 })
  }

  const handleClick_getMedia_alignments = async (a, b) => {
    const cb1 = (r) => {
      const _teamRecords = {}
      const { teamRecords } = r ?? {}
      if (teamRecords) {
        _.forEach(teamRecords, (tr, k) => {
          const _team = _.find(teams, { _itemKey: k })
          _teamRecords[k] = {
            ...tr[selectedYear],
            name: _team?.name
          }
        })
        const _csvr_results = ['name', 'level', 'section', 'record', 'overallRecord']
        const zzz = convertHelpers.convertArrayToCSV(_teamRecords, _csvr_results)
        console.log('zzz', zzz)
        convertHelpers.copyToClipboard(zzz)
      }

      console.log('r', r)
      updateMedia_localAlignment({ sportsKey: sportsKey, year: selectedYear, alignmentResults: r })
      createMediaSeason_handlers.handleStopUpdate()
    }
    createMediaSeason_handlers.handleStartUpdate()
    getMedia_alignments({ sportsYear: selectedYear + 1, callback: cb1 })
  }

  const handleClick_getMedia_records = async (a, b) => {
    const cb1 = (r) => { createMediaSeason_handlers.handleStopUpdate() }
    createMediaSeason_handlers.handleStartUpdate()
    getMedia_records({ sportsYear: selectedYear, yearCount: 15, callback: cb1 })
  }

  const handleClick_ammendMediaSeason = async (a, b) => {
    const _mediaSeason = mediaSeason_local?.data || mediaSeason_local
    const mediaSeason = _mediaSeason?.[selectedYear] || {}
    createSportsSeason_handlers.handleAmmend_sportsSeason_selected(mediaSeason, sportsSeason_selectedPdks)
  }

  const handleClick_fixSeasonals = async () => {
    createSportsSeason_handlers.handleFix_seasonals()
  }

  const handleClick_checkSeason = () => {

    const customComparator = (obj1, obj2) => {
      return (
        _.isEqual(obj1.away, obj2.away) &&
        _.isEqual(obj1.home, obj2.home) &&
        obj1.startDate === obj2.startDate &&
        obj1.levels === obj2.levels &&
        obj1.sections === obj2.sections
      );
    };

    const _m = _.sortBy(matches, 'startDate')
    const _mc = _.sortBy(matches_create, 'startDate')

    // Find the differences between the two lists
    const differencesInList1 = _.differenceWith(_m, _mc, customComparator);
    const differencesInList2 = _.differenceWith(_mc, _m, customComparator);

    console.log('Differences in List 1:', differencesInList1);
    console.log('Differences in List 2:', differencesInList2);

  }

  const handleShow = () => createSports_handlers.handleShow_importFromGs()

  useEffect(() => {
    const _stepValidations = {}
    Object.keys(_steps).forEach(key => {
      _stepValidations[key] = validateComponent(key)
    })
    setStepValidations(_stepValidations)
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [step]);


  const handleCreateSeasonFromPrevious = () => {
    const _selectedYearPlusOne = selectedYear + 1
    const _thisSeason = sportSeasonals[selectedYear]
    const { teams, sectionCounts, details } = _thisSeason
    const _newSeason = {
      teams, sectionCounts, details
    }
    createSportsSeason_handlers.handleSeason_createFromPrevious(_newSeason, _selectedYearPlusOne)
  }

  /**
  * THIS IS THE CLICK FOR THE COMMIT
  * @param {opts} clickOptions 
  */
  const handleClick_commitSeasonal = (clickOptions) => {
    const { clickType, updateType, isTemp, isBackup, isCommit, matchesOnly, teamsOnly } = clickOptions
    handleClick_updateSeasonal(null, null, updateType)
  }

  const handleClick_updateSeasonal = (a, b, updateType) => {

    const cbu = () => { createSportsSeason_handlers.handleStopUpdate() }

    createSportsSeason_handlers.handleStartUpdate()

    const opts = {
      sportsSeason_selected,
      selectedYear,
      _seasonalKey,
      _sportsAbr,
      sportsKey,
      sportPermissions,
      callback: cbu
    }

    if (updateType) {
      update_seasonalData({ opts, updateType: updateType })
      return;
    }
    // return
    switch (selectedSeasonal) {
      case _steps.details:
        update_seasonalData({ opts, updateType: seasonalUpdateTypes.details })
        break;
      case _steps.teams:
        update_seasonalData({ opts, updateType: seasonalUpdateTypes.teams })
        break;
      case _steps.alignment:
        update_seasonalData({ opts, updateType: seasonalUpdateTypes.teams })
        break;
      case _steps.sectionCrossovers:
        update_seasonalData({ opts, updateType: seasonalUpdateTypes.sectionCrossovers })
        break;
      case _steps.schedule:
        update_seasonalData({ opts, updateType: seasonalUpdateTypes.sectionDates })
        update_seasonalData({ opts, updateType: seasonalUpdateTypes.matches })
        break;
      default:
      // nothing
    }
  }

  const handleSet_modeRight = (mr) => setMode_right(mr)
  const handleClick_modeRight = (opts) => handleSet_modeRight(opts.rightMode)
  const handleOpenUrl_sport = () => openExternal.firebaseDbDirect(pathViews, ['sports', sportsKey])
  const handleOpenUrl_seasonal = (e) => openExternal.firebaseDbDirect(pathViews, ['_seasonals', sportsSeason_selectedPdks?.seasonals])
  const handleOpenUrl_seasonMatches = () => openExternal.firebaseDbDirect(pathViews, [_sportCollections._matches_seasonal, sportsSeason_selectedPdks?.matches_seasonal])
  const handleOpenUrl_teams = () => openExternal.firebaseDbDirect(pathViews, [_sportCollections._teams_seasonal, sportsSeason_selectedPdks?.teams_seasonal])

  const validateComponent = (key) => {
    const _name = key ? key : step.name
    switch (_name) {
      case _steps.details:
        return selectedYear && seasonStartDate ? true : false
      case _steps.qualifyCount:
        return qualifyReady
      case _steps.seasonCrossovers:
        return true
      // case _steps.seasonSchedule:
      //   return true
      default:
        break;
    }
  }

  const wvc_seasonSchedule = () => <WrapperVertCols mvc={2}>
    <CreateSectionDates createScheduleType={createScheduleTypes.season} teamCount={step.name} />
    <CreateSchedule viewItem={viewItem} handleActionPreview={handleActionPreview} />
  </WrapperVertCols>

  const content_seasonSchedule = () => <Wrapper
    header={'Schedule'}
    content={wvc_seasonSchedule()}
    wrapperType={wrapperTypes.padded}
  />

  const buttons_footer = () => {
    const btns_data = []
    const clickOptions_new = { clickType: 'createNew', isNew: true, updateType: 'createNew' }
    const clickOptions_backup = { clickType: 'backupSeason', isBackup: true, updateType: 'backupSeason' }
    const _selectedYearPlusOne = selectedYear + 1

    if (!_.includes(_sportSeasonalsKeys, _selectedYearPlusOne?.toString())) {
      btns_data.push({ oc: handleCreateSeasonFromPrevious, icon: 'save', caption: 'Create New Season From (' + latestSeason + ')', clickOptions: clickOptions_new, color: updateColor })
    }

    btns_data.push({ oc: handleClick_commitSeasonal, icon: 'save', caption: 'Backup Season (' + latestSeason + ')', clickOptions: clickOptions_backup, color: updateColor })
    btns_data.push({ oc: handleClick_fixSeasonals, icon: 'save', caption: 'Fix Seasonals', clickOptions: clickOptions_backup, color: updateColor })

    return { btns_data }
  }

  const footer_main = () => {

    const { btns_data } = buttons_footer()

    const btn_update = { opts: { rightMode: _rightModes.commitCurrent } }

    if (btns_data.length > 0) {

      const btns_right = []

      const caption_update = `Update ${selectedYear} Seasonal (${_.startCase(selectedSeasonal || '---')})`
      const caption_commit = `Update ${selectedYear} Season (${_.startCase(selectedSeasonal || '---')})`

      switch (selectedSeasonal) {
        case _steps.details:
        case _steps.alignment:
        case _steps.sectionCrossovers:
          btns_right.push({
            oc: handleClick_updateSeasonal,
            caption: caption_update,
            disabled: !selectedYear,
            color: updateColor,
            icon: 'save'
          })
          break;

        case _steps.teams:
          btns_right.push({
            oc: handleClick_updateSeasonal,
            caption: caption_update,
            icon: 'save',
            color: updateColor
          })
          break;

        case _steps.schedule:
          btns_right.push({
            oc: handleClick_updateSeasonal,
            caption: caption_update,
            icon: 'save',
            color: updateColor,

          })
          // btns_right.push(<UiSaveButtons save={{ caption: 'Update ' + selectedYear + ' Seasonal (' + _.startCase(seasonalUpdateTypes.sectionDates) + ')', oc: handleClick_updateSeasonal, disabled: !selectedYear, opts: btn_update.opts, color: updateColor, icon: 'save' }} />)

          break;

        default:
        // nothing
      }

      switch (selectedSeasonal) {
        case _steps.teams:
          btns_right.push({
            oc: handleClick_modeRight,
            caption: caption_commit,
            disabled: !selectedYear,
            opts: btn_update.opts,
            icon: 'arrow right'
          })
          // btns_right.push(<UiSaveButtons save={{ caption: 'Update ' + selectedYear + ' Season (' + _.startCase(selectedSeasonal ? selectedSeasonal : '---') + ')', oc: handleClick_modeRight, disabled: !selectedYear, opts: btn_update.opts, color: 'blue', icon: 'arrow right' }} />)
          break;

        case _steps.schedule:
          btns_right.push({
            oc: handleClick_modeRight,
            caption: caption_commit,
            disabled: !selectedYear,
            opts: btn_update.opts,
            icon: 'arrow right'
          })
          // btns_right.push(<UiSaveButtons 
          // save={{ caption: 'Update ' + selectedYear + ' Season (' + _.startCase(selectedSeasonal ? selectedSeasonal : '---') 
          // + ')', oc: handleClick_modeRight, disabled: !selectedYear, opts: btn_update.opts, color: 'blue', icon: 'arrow right' }} />)
          break;
        default:
        // nothing
      }

      return <div className={'header-flex'}>
        <FooterExternal
          _icons={_icons}
          _sportCollections={_sportCollections}
          districtLink={districtLink}
          googleSheetsId={googleSheetsId}
          latestSeason={latestSeason}
          mediaUrl={mediaUrl}
          selectedYear={selectedYear}
          sportsKey={sportsKey}
          sportsSeason_selectedPdks={sportsSeason_selectedPdks}
          _sportSeasonalsKeys={_sportSeasonalsKeys}
          updateColor={updateColor}
          handlers={
            {
              handleCreateSeasonFromPrevious,
              handleClick_commitSeasonal,
              handleClick_fixSeasonals,
              handleClick_getMedia_results,
              handleClick_getMedia_alignments,
              handleClick_ammendMediaSeason,
              handleClick_getGoogle_results
            }
          }
        />
        <div className={'header-flex'}>
          <UiSaveButtons others={btns_right} />
          {/* {btns_right} */}
        </div>
      </div>
    } else {
      return <UiSaveButtons save={{ caption: 'Update ' + selectedYear + ' Seasonal', oc: handleClick_modeRight, opts: btn_update.opts, color: 'blue', icon: 'arrow right' }} />
    }
  }

  const lblIcon = (caption, data, rm, ocdb, other) => {
    const oc = handleSet_modeRight
    const hasData = !_.isEmpty(data)
    return <div className={`lbl-key ${hasData ? 'hd' : ''}`} onClick={() => { oc && oc(rm) }}>
      <div>{caption}</div>
      {rm && <div><Icon
        name={ocdb && hasData ? 'external alternate' : 'ban'}
        color={ocdb && hasData ? 'orange' : 'grey'} onClick={(e) => {
          e.stopPropagation()
          ocdb && ocdb(e)
        }} />
      </div>}
      {other}
    </div>
  }

  const handleSave_selectedSeason = async () => {
    const cb = () => { }
    const _refPath = createRefPath_event(pathViews, ['sports', sportsKey])
    const dataToUpdate = { latestSeason: selectedYear }
    console.log('_refPath', _refPath, dataToUpdate)
    // database_fns.get_data({ refPath, callback: cb, opts: { returnFirstObject: true } })
    await database_fns.doc_set(_refPath, dataToUpdate, true)
  }

  const popupSelect = () => {
    const cb = () => {
      console.log('sportsSeason_handlers', sportsSeason_handlers)
    }
    return <PopupSelect items={['Set as Current']} dataItem={{}} handleMenuAction={handleSave_selectedSeason} icon={'options'} />

  }


  const header_top = useMemo(() => {
    return <div className={'header-flex'}>
      {lblIcon(`Sport - ${_.startCase(_sportsModeSport)} - ${_sportsAbr}`, _currentSport, _rightModes.currentSport, handleOpenUrl_sport)}
      {lblIcon('Seasonal', sportsSeason_selectedPdks?.seasonals, _rightModes.currentSeason, handleOpenUrl_seasonal)}
      {lblIcon('Seasonal Teams', sportsSeason_selectedPdks?.teams_seasonal, _rightModes.currentTeams, handleOpenUrl_teams)}
      {lblIcon('Seasonal Matches', sportsSeason_selectedPdks?.matches_seasonal, _rightModes.currentMatches, handleOpenUrl_seasonMatches)}
    </div>
  }, [sportsSeason_selectedPdks, selectedYear])


  const wrapperContent = (content) => <Suspense fallback={<SuspenseDimmer origin={'Create Season'} />}>
    <Wrapper
      content={content}
      wrapperType={wrapperTypes.paddedHeaderAndFooter}
      updating={_updating}
      css={{ container: 'app-dark-mode' }}
    />
  </Suspense>

  const fallbackCopy = (text) => {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "absolute";
    textarea.style.left = "-9999px";
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand("copy");
      console.log("Copied using fallback!");
    } catch (err) {
      console.error("Fallback copy failed", err);
    }
    document.body.removeChild(textarea);
  };

  /**
   * 
   * @returns content for the right sidebar
   */
  const content_sidebarRight = () => {

    let headerCaption = _.startCase(mode_right)
    let headerCount = 0;
    let wrapperContent_right;

    let _seasonalUpdateType;

    let _json = sportsSeason_db;
    let _scNames = false
    let _subNames = false
    let _subName = false
    let _showKey = false

    const { sportsSeason_selected, sportsSeason_selectedPdks } = createSportsSeason_state ?? {}
    const { matches, seasonals } = sportsSeason_selected ?? {}
    const { teams } = seasonals ?? {}

    switch (selectedSeasonal) {
      // case _steps.details:
      //   _json = sportsSeason_db?.details
      //   break;

      case _steps.teams:
        _json = teams
        _scNames = true
        _subNames = true
        _subName = true
        _showKey = true
        _seasonalUpdateType = seasonalUpdateTypes.fullSeason_teams
        break;

      case _steps.schedule:
        const _allMatches = _.cloneDeep(matches)
        _.forEach(_allMatches, (m) => {
          m.sections = m.sections.replace('section', '')
        })
        headerCount = _.size(_allMatches)
        _json = _allMatches
        // const _csv_match = ['id', '_itemKey', 'startDate', 'section', 'level', 'away', 'awayScore', 'home', 'homeScore', 'extra', 'oldStartDate', 'postponed', 'cancelled', 'exhibition']
        // const xxx = sportsHelpers.getMatches_flat(_allMatches)
        // const csv = convertHelpers.convertArrayToCSV(xxx, _csv_match)
        // fallbackCopy(csv)
        _seasonalUpdateType = seasonalUpdateTypes.fullSeason_matches
        break;

      default:
      // nothing
    }

    wrapperContent_right = <TreeViewer asSquares={true} data={_json} scNames={_scNames} ignoreAlpha={true} subName={_subName} subNames={_subNames} showKey={_showKey} />

    const clickOptions_save = { updateType: _seasonalUpdateType }

    const _btns = <UiSaveButtons
      save={{
        oc: handleClick_commitSeasonal,
        caption: `${headerCaption} (${_seasonalUpdateType})`,
        color: updateColor,
        opts: clickOptions_save
      }} />

    const header_wrapper = <div className='header-flex'>
      <div>{headerCaption}</div>
      <Label>{headerCount}</Label>
    </div>

    return <Wrapper
      header={header_wrapper}
      content={wrapperContent_right}
      footer={_btns}
      wrapperType={wrapperTypes.paddedHeaderAndFooter}
      updating={_updating}
    />
  }

  /**
   * 
   * @param {*} visible 
   * @returns DateSidebar for the right content (if visible)
   */
  const sidebar_right = (visible) => <DataSidebar
    direction={'right'}
    visible={visible}
    content={visible && content_sidebarRight()}
    onHide={handleSet_modeRight}
  />

  const sidebar = (content) => <Sidebar.Pushable style={{ overflow: 'hidden' }}>
    {sidebar_right(mode_right ? true : false)}
    <Sidebar.Pusher dimmed={mode_right ? true : false} className={'h100'}>
      {wrapperContent(content)}
    </Sidebar.Pusher>
  </Sidebar.Pushable>

  const content = () => {
    switch (selectedSeasonal) {
      case _steps.details:
        return sidebar(<CreateSportsDetails subActionType={subActionType} />)
      case _steps.teams:
        return sidebar(<CreateSportSeasonTeams />)
      case _steps.alignment:
        return sidebar(<CreateSportSeasonAlignment />)
      case _steps.sectionCrossovers:
        return sidebar(<CreateCrossoverSections />)
      case _steps.schedule:
        return sidebar(content_seasonSchedule())
      default:
        return <div></div>
    }
  }

  let cn_container = 'create-season'
  if (_seasonalId) { cn_container += ' verif' }

  // changes the year
  const menu_vert_years = useMemo(() => {

    const dataMenuItems = subActionType === _asat.editSeason ? [{ name: latestSeason }] : listYears(1, 10, false, true)

    _.forEach(dataMenuItems, (dmi) => {
      const hasSeasonal = sportSeasonals && sportSeasonals[dmi?.name] ? true : false
      const hasMedia = mediaResults_local && mediaResults_local?.[dmi?.name] ? true : false
      if (hasSeasonal && hasMedia) {
        dmi.icon = 'check square'
        dmi.iconColor = 'green'
      } else if (hasSeasonal) {
        dmi.icon = 'check circle'
        dmi.iconColor = 'green'
      } else if (hasMedia) {
        dmi.icon = 'check circle outline'
        dmi.iconColor = 'green'
      }
    })

    return <Wrapper
      header={'Year'}
      content={
        <MenuVert
          activeMenuItem={selectedYear}
          asSquares={true}
          menuItems={dataMenuItems}
          oc={handleActive_year}
        />
      }
    />
  }, [selectedYear, sportSeasonals, mediaResults_local])

  const menu_vert_steps = useMemo(() => {
    return <Wrapper
      header={<div className='header-flex'>
        <div>{`Seasonal - ${selectedYear ? selectedYear : 'None'}`}</div>
        <div>{popupSelect()}</div>
      </div>}
      content={<MenuVert
        activeMenuItem={selectedSeasonal}
        asSquares={true}
        menuItems={steps}
        oc={handleActive_seasonal}
      />}
    />
  }, [selectedSeasonal, selectedYear])

  const wrapper_main = () => <Wrapper
    content={content()}
  />

  // contents. horizontal
  const wvc_season = () => <WrapperVertCols mvc={`season`}  >
    {menu_vert_years}
    {menu_vert_steps}
    {wrapper_main()}
  </WrapperVertCols>

  // main wrapper. vertical
  const content_sport = () => <Wrapper
    header={header_top}
    content={wvc_season()}
    footer={footer_main()}
    wrapperType={wrapperTypes.paddedHeaderAndFooter}
    updating={_updating}
    css={{ container: 'app-dark-mode' }}
  />

  const contentWithProvider = () => <CreateSportsSeasonContext.Provider value={{ createSportsSeason_state, createSportsSeason_handlers }}>
    <CreateMediaSeasonContext.Provider value={{ createMediaSeason_state, createMediaSeason_handlers }}>
      {content_sport()}
    </CreateMediaSeasonContext.Provider>
  </CreateSportsSeasonContext.Provider>

  return step ? contentWithProvider() : <DimmerPending />
}

export default CreateSeason